import React, { useState, useMemo } from 'react';
import { Search, Trash2, Download, FileText, File, Image, Archive, Code, Table, Presentation, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { adminFileService } from '@/services/adminFileService';
import type { CourseFile, FileLevelType, FileType } from '@/types/courseFile.types';

interface FileManagementTableProps {
  files: CourseFile[];
  isLoading: boolean;
  onDelete: (fileId: string) => Promise<void>;
  onRefresh: () => void;
}

const FILE_TYPE_ICONS: Record<FileType, React.ElementType> = {
  pdf: FileText,
  document: File,
  spreadsheet: Table,
  presentation: Presentation,
  image: Image,
  archive: Archive,
  code: Code,
  text: FileText,
  other: File,
};

const FILE_TYPE_COLORS: Record<FileType, string> = {
  pdf: 'text-red-400 bg-red-400/20',
  document: 'text-blue-400 bg-blue-400/20',
  spreadsheet: 'text-green-400 bg-green-400/20',
  presentation: 'text-orange-400 bg-orange-400/20',
  image: 'text-purple-400 bg-purple-400/20',
  archive: 'text-yellow-400 bg-yellow-400/20',
  code: 'text-cyan-400 bg-cyan-400/20',
  text: 'text-gray-400 bg-gray-400/20',
  other: 'text-gray-400 bg-gray-400/20',
};

const LEVEL_TYPE_COLORS: Record<FileLevelType, string> = {
  module: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  lesson: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  segment: 'bg-green-500/20 text-green-400 border-green-500/30',
  topic: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
};

type SortField = 'uploaded_at' | 'file_size' | 'title' | 'level_type';
type SortDirection = 'asc' | 'desc';

export default function FileManagementTable({
  files,
  isLoading,
  onDelete,
  onRefresh,
}: FileManagementTableProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<SortField>('uploaded_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Filter and sort files
  const filteredFiles = useMemo(() => {
    let result = [...files];

    // Filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(f =>
        f.title.toLowerCase().includes(query) ||
        f.original_filename.toLowerCase().includes(query) ||
        f.level_id.toLowerCase().includes(query) ||
        f.level_type.toLowerCase().includes(query)
      );
    }

    // Sort
    result.sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'uploaded_at':
          comparison = new Date(a.uploaded_at).getTime() - new Date(b.uploaded_at).getTime();
          break;
        case 'file_size':
          comparison = a.file_size - b.file_size;
          break;
        case 'title':
          comparison = a.title.localeCompare(b.title);
          break;
        case 'level_type':
          comparison = a.level_type.localeCompare(b.level_type);
          break;
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return result;
  }, [files, searchQuery, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const handleDelete = async (fileId: string) => {
    setDeleting(true);
    try {
      await onDelete(fileId);
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Delete failed:', error);
    } finally {
      setDeleting(false);
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ?
      <ChevronUp className="w-4 h-4" /> :
      <ChevronDown className="w-4 h-4" />;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
        <input
          type="text"
          placeholder="Search files by title, filename, level..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-3 bg-slate-800/50 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500"
        />
      </div>

      {/* Table */}
      {filteredFiles.length === 0 ? (
        <div className="text-center py-12">
          <FileText className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400">No files found</p>
          {searchQuery && (
            <p className="text-sm text-gray-500 mt-2">Try adjusting your search</p>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">File</th>
                <th
                  className="text-left py-3 px-4 text-sm font-medium text-gray-400 cursor-pointer hover:text-white"
                  onClick={() => handleSort('level_type')}
                >
                  <div className="flex items-center gap-1">
                    Level <SortIcon field="level_type" />
                  </div>
                </th>
                <th
                  className="text-left py-3 px-4 text-sm font-medium text-gray-400 cursor-pointer hover:text-white"
                  onClick={() => handleSort('file_size')}
                >
                  <div className="flex items-center gap-1">
                    Size <SortIcon field="file_size" />
                  </div>
                </th>
                <th
                  className="text-left py-3 px-4 text-sm font-medium text-gray-400 cursor-pointer hover:text-white"
                  onClick={() => handleSort('uploaded_at')}
                >
                  <div className="flex items-center gap-1">
                    Uploaded <SortIcon field="uploaded_at" />
                  </div>
                </th>
                <th className="text-right py-3 px-4 text-sm font-medium text-gray-400">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredFiles.map((file) => {
                const Icon = FILE_TYPE_ICONS[file.file_type as FileType] || File;
                const iconColor = FILE_TYPE_COLORS[file.file_type as FileType] || FILE_TYPE_COLORS.other;
                const levelColor = LEVEL_TYPE_COLORS[file.level_type as FileLevelType];

                return (
                  <tr key={file.id || file._id} className="border-b border-white/5 hover:bg-white/5">
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${iconColor}`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div>
                          <p className="text-white font-medium">{file.title}</p>
                          <p className="text-sm text-gray-500">{file.original_filename}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <span className={`px-2 py-1 text-xs font-medium rounded border ${levelColor}`}>
                        {file.level_type}
                      </span>
                      <span className="text-gray-500 text-sm ml-2">{file.level_id}</span>
                    </td>
                    <td className="py-4 px-4 text-gray-400">
                      {adminFileService.formatFileSize(file.file_size)}
                    </td>
                    <td className="py-4 px-4 text-gray-400">
                      {new Date(file.uploaded_at).toLocaleDateString()}
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center justify-end gap-2">
                        {file.download_url && (
                          <a
                            href={file.download_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                            title="Download"
                          >
                            <Download className="w-4 h-4 text-gray-400 hover:text-white" />
                          </a>
                        )}
                        <button
                          onClick={() => setDeleteConfirm(file.id || file._id || '')}
                          className="p-2 hover:bg-red-500/20 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4 text-gray-400 hover:text-red-400" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/70" onClick={() => setDeleteConfirm(null)} />
          <div className="relative bg-slate-900 border border-white/10 rounded-xl p-6 max-w-md w-full m-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-500/20 rounded-lg">
                <AlertCircle className="w-6 h-6 text-red-400" />
              </div>
              <h3 className="text-lg font-bold text-white">Delete File?</h3>
            </div>
            <p className="text-gray-400 mb-6">
              This action cannot be undone. The file will be permanently removed from storage.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
                className="px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-white transition-all"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-white transition-all disabled:opacity-50"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
